import React, { useState, useEffect, useContext } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { AuthContext } from "../../Context/AuthContext";
import { logActivity } from "../../utils/LogActivity";
import { Authorization } from "../../Context/Authorization";

const EditRangeForm = ({ range, onClose }) => {
  const [rangeName, setRangeName] = useState(range.rangeName);
  const [rangeCode, setRangeCode] = useState(range.rangeCode);
  const [divisionId, setDivisionId] = useState(range.divisionId || "");
  const [division, setDivision] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const { access, userId } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  const validateForm = () => {
    const newErrors = {};
    if (!rangeName) {
      newErrors.rangeName = "Range name is required";
    }
    if (!rangeCode) {
      newErrors.rangeCode = "Range code is required";
    }
    if (!divisionId) {
      newErrors.divisionId = "Please select Division Id";
    }
    return newErrors;
  };

  useEffect(() => {
    setRangeName(range.rangeName);
    setRangeCode(range.rangeCode);
    setDivisionId(range.divisionId?._id || "");

    setLoading(true);
    const fetchDivisions = async () => {
      try {
        const config = Authorization();
        const response = await fetch(`${Api}/api/divisions`, config);
        const data = await response.json();

        if (Array.isArray(data)) {
          setDivision(data);

          // Ensure `divisionId` is properly set if not already initialized
          if (!divisionId) {
            setDivisionId(range.divisionId || "");
          }
        } else {
          console.error("API did not return an array:", data);
        }
      } catch (error) {
        console.error("Error fetching divisions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDivisions();
  }, [access.divisionId, range.divisionId, divisionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      toast.error("Please fill all Details");
      return;
    }
    const confirmEdit = window.confirm(
      "Are you sure you want to Edit this Range?"
    );
    if (!confirmEdit) {
      return;
    }

    const formData = { rangeName, rangeCode, divisionId };

    try {
      const config = Authorization();
      const response = await fetch(`${Api}/api/ranges/${range._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...config.headers },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        const result = await response.json();
        await logActivity(userId, "Edited Range", "State Page", Api);
        console.log("Success:", result);
        toast.success("Submitted Successfully");
        onClose();
      } else {
        console.error("Error submitting form:", response.statusText);
        toast.error("Error submitting form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Error submitting form");
    }
  };

  return (
    <div>
      {loading && (
        <div className="loading-container">
          <CircularProgress />
        </div>
      )}
      <Modal show onHide={onClose}>
        <Modal.Header>
          <Modal.Title>Edit Range</Modal.Title>
          <Button
            variant="outline-danger"
            onClick={onClose}
            style={{ marginLeft: "auto" }}
          >
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="formRangeName">
              <Form.Label>
                Range Name<span className="required-asterisk">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter range name"
                value={rangeName}
                onChange={(e) => setRangeName(e.target.value)}
                isInvalid={!!errors.rangeName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.rangeName}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="formRangeCode">
              <Form.Label>
                Range Code<span className="required-asterisk">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter range code"
                value={rangeCode}
                onChange={(e) => setRangeCode(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {errors.rangeCode}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="formDivisionId">
              <Form.Label>
                Division ID<span className="required-asterisk">*</span>
              </Form.Label>
              <Form.Control
                as="select"
                value={divisionId}
                onChange={(e) => setDivisionId(e.target.value)}
                isInvalid={!!errors.divisionId}
              >
                <option value="">Select Division</option>
                {division.map((state) => (
                  <option key={state._id} value={state._id}>
                    {state.divisionCode}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.divisionId}
              </Form.Control.Feedback>
            </Form.Group>

            <button
              type="submit"
              className="btn btn-outline-success btn-icon-text mt-3"
            >
              Edit
              <i className="mdi mdi-file-check btn-icon-append"></i>
            </button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EditRangeForm;
